import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import jobService from '../../services/jobService';
import { JobCard } from '../../pages/user/jobs/JobListing';

const LATEST_LIMIT = 3;

/**
 * LatestJobsSection — dashboard block showing the 3 newest job notices (from
 * /jobs) with a "view all" link. Reuses the exact JobCard from the listing so
 * the design stays in sync. Renders nothing when there are no jobs to show.
 */
export default function LatestJobsSection() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        jobService
            .getJobs({ sort: 'new', page: 1, limit: LATEST_LIMIT, expired: 'exclude' })
            .then((res) => {
                if (!mounted) return;
                setJobs(res.data || []);
            })
            .catch(() => {
                if (mounted) setJobs([]);
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });
        return () => {
            mounted = false;
        };
    }, []);

    // Nothing to show once loaded → hide the whole section.
    if (!loading && jobs.length === 0) return null;

    return (
        // Same section language as the "Recently edited photos" and income blocks: a softly tinted gradient panel,
        // a plain semibold title with a "View all →" text link, and white rounded-2xl cards inside.
        <div className="!mt-8 sm:!mt-12 bg-[linear-gradient(135deg,#10b98114,#0ea5e914,#3a86ff14)] dark:bg-none dark:bg-zinc-800/60 border border-emerald-500/20 dark:border-zinc-700 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <span className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                    {t('dashboard.latestJobs.title')}
                </span>
                <button
                    type="button"
                    onClick={() => navigate('/jobs')}
                    className="text-[13px] font-semibold text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors"
                >
                    {t('dashboard.latestJobs.viewAll')} →
                </button>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 lg:grid-cols-3">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div
                            key={i}
                            className="h-[168px] animate-pulse rounded-2xl border border-zinc-200/80 bg-white/70 dark:border-zinc-700 dark:bg-zinc-800"
                        />
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 lg:grid-cols-3">
                    {jobs.map((job) => (
                        <JobCard
                            key={job.id}
                            job={job}
                            t={t}
                            variant="dashboard"
                            showSave={false}
                            onOpen={() => navigate(`/jobs/${job.slug || job.id}`)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
